/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { func, object, string } from 'prop-types';

import QASAddressContainer from './QASAddressContainer';

import { trackEvent } from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getQasAddressSuggestions, getShowValidatedAddress } from '../../../../../../../state/ducks/MfeCheckout/ducks/CartItem/AddressQAS/AddressQAS-Selectors';
import { flagOffShowValidatedQAS } from '../../../../../../../state/ducks/MfeCheckout/ducks/CartItem/AddressQAS/AddressQAS-Actions';
import { getFeatureFlags, getModalQasRadioBtnSFABTest } from '../../../../../../../state/ducks/MfeCheckout/ducks/App/App-Selectors';
import { getFormattedRecipientData } from '../Helpers/Input-Helper';
import { enterpriseSaveAddressBook, enterpriseSaveRecipientGiftMessage } from '../../../../../../../state/ducks/MfeCheckout/ducks/CartItem/Recipient/Recipient-Actions';
import { anchorToSection } from '../Helpers/Helper';
import { getOrderId } from '../../../../../../../state/ducks/MfeCheckout/ducks/Cart/Cart-Selectors';
import QASAddressContainerV2 from './QASAddressContainerV2';
import useClickStreamCustomEventsTracking from '../../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { emitClickstreamCheckoutEvent } from '../../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';

const QasAddressVerificationModalV2 = ({
    handleCloseAddressBook,
    history,
    orderItem,
    pageType,
    parentType,
    recipientGreetingData,
    recipientQASData,
    recipientRedirectTo,
    setButtonTypeQAS,
    setItemRecipientData,
    setRecipientGreetingData,
    typeData,
    orderItemError,
}) => {
    const dispatchAction = useDispatch();

    const cartId = useSelector(getOrderId);
    const featureFlags = useSelector(getFeatureFlags);
    const suggestionList = useSelector(getQasAddressSuggestions);
    const showAddressValidation = useSelector(getShowValidatedAddress);
    const modalQasRadioButtonsSFABTest = useSelector(getModalQasRadioBtnSFABTest);

    const [addressSelected, setAddressSelected] = useState({
        address: {},
        index: null,
        isKeepAddress: false,
    });

    let QASAddressContainerComponent = QASAddressContainer;
    if (
        featureFlags['is-qas-modal-with-radio-buttons-enabled'] === 'newflow'
        || (
            featureFlags['is-qas-modal-with-radio-buttons-enabled'] === 'salesforce'
            && modalQasRadioButtonsSFABTest?.campaign?.campaignResponses?.[0]?.userGroup === 'Default'
        )
    ) {
        QASAddressContainerComponent = QASAddressContainerV2;
    }

    const isFireImpression = featureFlags['is-qas-modal-with-radio-buttons-enabled'] === 'salesforce'
        && modalQasRadioButtonsSFABTest?.campaign?.campaignResponses?.[0]?.userGroup;

    useClickStreamCustomEventsTracking({
        salesforceResponse: isFireImpression ? modalQasRadioButtonsSFABTest : null,
        isFireImpression,
        page: { type: pageType },
    });

    if (!featureFlags['is-qas-address-verification-enabled']) {
        return null;
    }

    useEffect(() => {
        if (suggestionList.suggestions?.length > 0) {
            setAddressSelected({
                address: suggestionList.suggestions[0],
                index: 0,
                isKeepAddress: false,
            });
        }

        dispatchAction(trackEvent({
            eventCategory: 'Checkout',
            eventAction: 'QAS Modal',
            eventLabel: 'Impression V2',
        }));

        dispatchAction(emitClickstreamCheckoutEvent({
            action: 'Impression',
            label: 'Open QAS modal',
            eventType: 'clickstream.checkout-address',
            eventSubType: 'qas-modal-open',
            page: { type: pageType },
            order: { cartId },
            recipients: [
                {
                    userAddress: {
                        address1: recipientQASData.address1,
                        address2: recipientQASData.address2,
                        city: recipientQASData.city,
                        state: recipientQASData.state,
                        zipcode: recipientQASData.zipCode,
                        country: recipientQASData.country,
                    },
                    qasAddress: {
                        address1: '',
                        address2: '',
                        city: '',
                        state: '',
                        country: '',
                        zipcode: '',
                    },
                    addressSource: parentType,
                    qasSuggestionsCount: suggestionList.suggestions?.length || 0,
                },
            ],
        }));
    }, []);

    const handleClose = (isClose, recipientData, reason, isError) => {
        if (recipientData?.firstName) {
            const recipientInfo = { ...recipientData, reason: reason !== 'addressBook' ? 'QAS' : null, isError };
            setItemRecipientData(recipientInfo);
            setRecipientGreetingData({});
        }

        // Close addressbook and qas
        handleCloseAddressBook(isClose);
        dispatchAction(flagOffShowValidatedQAS());
    };

    const handleSubmitAddress = (type = '', dpvIndicator = 'N', isAddressVerified = 'O', addressQASSelected = {}) => {
        const formattedRecipientData = getFormattedRecipientData(recipientQASData, false);

        if (parentType === 'addressbook') {
            const reqObj = {
                recipient: recipientQASData,
                orderItemId: orderItem.cartItemId,
                typeQAS: type,
                addressQASSelected,
                parentType,
                handleCallBack: handleClose,
                cartLocationType: orderItem.deliveryInfo?.locationType,
                itemDTWData: orderItem.deliveryInfo?.deliveryTimeWindow,
            };
            // send in the call back the address selected back and format this one and populate the data
            // according the selection.
            // populate the data through context to recipientForm
            setItemRecipientData(formattedRecipientData);

            dispatchAction(enterpriseSaveAddressBook(reqObj));
        } else {
            // this happend when the user dont select any cgc, card isle or giftmessage
            let greetingData = recipientGreetingData;
            if (!recipientGreetingData?.type && !orderItem?.greetingMessage?.greetingMessage) {
                greetingData = {
                    type: 'giftMessage',
                    data: {
                        message: '',
                        signature: '',
                        occasionCode: '',
                    },
                };
            }

            // save recipient
            const reqObj = {
                recipient: formattedRecipientData,
                history,
                orderItemId: orderItem.cartItemId,
                orderItemIndex: orderItem.orderItemIndex,
                dpvIndicator,
                isAddressVerified,
                checkSameAsDeliveryAddress: recipientQASData.checkSameAsDeliveryAddress,
                useAddressForAll: recipientQASData.useAddressForAll,
                isDigitalProduct: false,
                pageType,
                greetingData,
                pageToRedirect: recipientRedirectTo,
                brandCode: orderItem.product?.brandCode,
                typeQAS: type,
                parentType,
                addressQASSelected,
                handleCallBack: handleClose, // TODO: i cant find who set this-> recipientQASData.handleCallBack,
                cartLocationType: orderItem.deliveryInfo?.locationType,
                cartZipCode: orderItem.deliveryInfo?.zipCode,
                itemDTWData: orderItem.deliveryInfo?.deliveryTimeWindow,
            };
            setItemRecipientData(formattedRecipientData);
            dispatchAction(enterpriseSaveRecipientGiftMessage(reqObj));
            dispatchAction(flagOffShowValidatedQAS());
        }

        anchorToSection(`#orderItem-${orderItem.cartItemId}`, 20, false);
    };

    const handleSelectedAddress = (address, index) => {
        setAddressSelected({
            address,
            index,
            isKeepAddress: false,
        });

        dispatchAction(trackEvent({
            eventCategory: 'Checkout',
            eventAction: 'Clicked on Address',
            eventLabel: '',
        }));
    };

    const handleKeepCurrentAddress = () => {
        setAddressSelected({
            address: {},
            index: null,
            isKeepAddress: true,
        });
    };

    const editVerificationQAS = () => {
        anchorToSection(`#orderItem-${orderItem.cartItemId}`, 20, false, 0);
        dispatchAction(flagOffShowValidatedQAS());
        setButtonTypeQAS('editAddress');

        dispatchAction(trackEvent({
            eventCategory: 'Checkout',
            eventAction: 'Address Verification Modal',
            eventLabel: 'Edit Address',
        }));

        dispatchAction(emitClickstreamCheckoutEvent({
            action: 'Click',
            label: 'Close/Edit Button QAS Modal',
            eventType: 'clickstream.checkout-address',
            eventSubType: 'qas-modal-exit',
            page: { type: pageType },
            order: { cartId },
            recipients: [
                {
                    userAddress: {
                        address1: recipientQASData.address1,
                        address2: recipientQASData.address2,
                        city: recipientQASData.city,
                        state: recipientQASData.state,
                        zipcode: recipientQASData.zipCode,
                        country: recipientQASData.country,
                    },
                    qasAddress: {
                        address1: '',
                        address2: '',
                        city: '',
                        state: '',
                        country: '',
                        zipcode: '',
                    },
                    addressSource: parentType,
                    qasSuggestionsCount: suggestionList.suggestions?.length || 0,
                },
            ],
        }));
    };

    const saveVerificationQAS = () => {
        if (suggestionList.suggestions?.length > 0 && !addressSelected.isKeepAddress) {
            let address = addressSelected.address;
            if (suggestionList.suggestions.length === 1) {
                address = suggestionList.suggestions[0];
            }

            handleSubmitAddress('qasAddressSelection', 'N', 'V', address);
            setButtonTypeQAS('qasAddressSelection');
            dispatchAction(trackEvent({
                eventCategory: 'Checkout',
                eventAction: 'Clicked on Address',
                eventLabel: '',
            }));

            dispatchAction(trackEvent({
                eventCategory: 'Checkout',
                eventAction: 'QAS modal',
                eventLabel: 'Verified',
            }));
            let qasAddress = {};

            if (recipientQASData.country === 'USA') {
                const addressInfo = address.text.split(',');
                const addressCityStateZip = addressInfo?.[1] || [];
                if (addressCityStateZip.length > 2) {
                    qasAddress = {
                        address1: addressInfo[0].trim() || '',
                        address2: '',
                        city: addressCityStateZip[0].trim() || '',
                        state: addressCityStateZip[1].trim() || '',
                        country: recipientQASData.country,
                        zipcode: addressCityStateZip[2].trim() || '',
                    };
                }
            } else if (recipientQASData.country === 'CAN') {
                const addressInfo = address.text.split(',');
                if (addressInfo.length > 3) {
                    qasAddress = {
                        address1: addressInfo[0].trim() || '',
                        address2: '',
                        city: addressInfo[1].trim() || '',
                        state: addressInfo[2].trim() || '',
                        country: recipientQASData.country,
                        zipcode: addressInfo[3].trim() || '',
                    };
                }
            }

            dispatchAction(emitClickstreamCheckoutEvent({
                action: 'Click',
                label: 'Click from the suggestion list',
                eventType: 'clickstream.checkout-address',
                eventSubType: 'qas-modal-list-selection',
                page: { type: pageType },
                order: { cartId },
                recipients: [
                    {
                        userAddress: {
                            address1: recipientQASData.address1,
                            address2: recipientQASData.address2,
                            city: recipientQASData.city,
                            state: recipientQASData.state,
                            zipcode: recipientQASData.zipCode,
                            country: recipientQASData.country,
                        },
                        qasAddress,
                        addressSource: parentType,
                        qasSuggestionsCount: suggestionList.suggestions?.length || 0,
                    },
                ],
            }));
        } else {
            handleSubmitAddress('keep', 'N', 'O');
            setButtonTypeQAS('keep');
            anchorToSection(`#orderItem-${orderItem.cartItemId}`, 20, false);
            dispatchAction(trackEvent({
                eventCategory: 'Checkout',
                eventAction: 'Address Verification Modal',
                eventLabel: 'Keep Address',
            }));

            dispatchAction(trackEvent({
                eventCategory: 'Checkout',
                eventAction: 'QAS modal',
                eventLabel: 'Overridden',
            }));

            dispatchAction(emitClickstreamCheckoutEvent({
                action: 'Click',
                label: 'Click to Keep the address',
                eventType: 'clickstream.checkout-address',
                eventSubType: 'qas-modal-keep',
                page: { type: pageType },
                order: { cartId },
                recipients: [
                    {
                        userAddress: {
                            address1: recipientQASData.address1,
                            address2: recipientQASData.address2,
                            city: recipientQASData.city,
                            state: recipientQASData.state,
                            zipcode: recipientQASData.zipCode,
                            country: recipientQASData.country,
                        },
                        qasAddress: {
                            address1: '',
                            address2: '',
                            city: '',
                            state: '',
                            country: '',
                            zipcode: '',
                        },
                        addressSource: parentType,
                        qasSuggestionsCount: suggestionList.suggestions?.length || 0,
                    },
                ],
            }));
        }
    };

    if (!featureFlags['is-qas-address-verification-enabled']) {
        return null;
    }

    return (
        <QASAddressContainerComponent
            addressItemPosition="text"
            addressSelected={addressSelected}
            editVerificationQAS={editVerificationQAS}
            featureFlags={featureFlags}
            handleKeepCurrentAddress={handleKeepCurrentAddress}
            handleSelectedAddress={handleSelectedAddress}
            orderItemError={orderItemError}
            recipientQASData={recipientQASData}
            saveVerificationQAS={saveVerificationQAS}
            showAddressValidation={showAddressValidation}
            suggestionList={suggestionList?.suggestions || []}
            typeData={typeData}
            versionQAS="v2"
        />
    );
};

QasAddressVerificationModalV2.propTypes = {
    handleCloseAddressBook: func.isRequired,
    history: object.isRequired,
    orderItem: object.isRequired,
    pageType: string.isRequired,
    parentType: string.isRequired,
    recipientQASData: object.isRequired,
    recipientGreetingData: object.isRequired,
    recipientRedirectTo: string.isRequired,
    setButtonTypeQAS: func.isRequired,
    setItemRecipientData: func.isRequired,
    setRecipientGreetingData: func.isRequired,
    typeData: string,
    orderItemError: string,
};

QasAddressVerificationModalV2.defaultProps = {
    typeData: '',
    orderItemError: null,
};
export default QasAddressVerificationModalV2;
